/**
 * Integrytas Group - Base App (MOBILE)
 *
 * Registry of MOBILE child editors for the related-children drill-down.
 *
 * Separate from the desktop registry on purpose. §16 requires the two
 * presentation layers to be independent, and the entries hold COMPONENTS — a
 * shared registry would let a desktop modal be resolved on a phone, which is
 * exactly what §16 forbids. The shared parts (the RBAC decision, the read-only
 * fieldset guard) live in src/lib/child_editors and are reused; only the
 * component mapping is duplicated, and that duplication is the point.
 *
 * Keyed by table name, matching the group's `table` — so a mobile group already
 * wired for the generic sheet gains its editor the moment one registers.
 *
 * Registration is a module-load side effect: mobile_page_components.tsx imports
 * every mobile page statically, so editors are registered before mount and no
 * page imports another.
 */
import type { ComponentType } from 'react';
import type { AuthSession } from '../../types/auth';

export interface MobileChildEditorProps {
  session: AuthSession;
  pk: string | number;
  /** True when the caller lacks can_change on the CHILD's page. */
  readOnly: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export interface MobileChildEditorEntry {
  /** The CHILD's page internal_code — the RBAC subject. */
  pageCode: string;
  Editor: ComponentType<MobileChildEditorProps>;
}

const REGISTRY = new Map<string, MobileChildEditorEntry>();

export function registerMobileChildEditor(table: string, entry: MobileChildEditorEntry): void {
  REGISTRY.set(table, entry);
}

export function getMobileChildEditor(table: string | undefined): MobileChildEditorEntry | null {
  if (!table) return null;
  return REGISTRY.get(table) ?? null;
}

export function registeredMobileChildTables(): string[] {
  return [...REGISTRY.keys()].sort();
}
